import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SeamlessGameFrame } from '@/components/lobby/SeamlessGameFrame.js';
import { LotterySideRail, type LotteryRailView } from '@/components/lobby/LotterySideRail.js';
import { EmptyState } from '@/components/lobby/EmptyState.js';
import { Button } from '@/components/ui/button.js';
import { useAuthStore } from '@/stores/auth.js';
import { mapApiError } from '@/lib/api/error.js';
import { launchGame, PopupBlockedError } from './launchGame.js';
import { fetchGames, GAMES_QUERY_KEYS, type Game, type LaunchMethod } from './api.js';

export type LotteryEmbedView =
  | { kind: 'game'; code: string }
  | { kind: 'tickets' }
  | { kind: 'results' };

interface Embed {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
}

/**
 * Route-backed lottery shell: resolves the target from the catalog, launches
 * the SUPERM frameless view, and mounts the iframe with our own side rail.
 * Used by /lottery/$code (a game board) and /lottery/tickets|results
 * (provider-wide deep views, launched with options.path).
 */
export function LotteryEmbedPage({ view }: { view: LotteryEmbedView }) {
  const { t, i18n } = useTranslation(['casino', 'common', 'games']);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [embed, setEmbed] = useState<Embed | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const launchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!accessToken) void navigate({ to: '/login' });
  }, [accessToken, navigate]);

  const gamesQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.list({ category: 'lottery' }),
    queryFn: () => fetchGames({ category: 'lottery', pageSize: 200 }),
    staleTime: 30_000,
    enabled: Boolean(accessToken),
  });

  const items = (gamesQuery.data?.items ?? []) as Game[];
  // Deep views are provider-wide; any lottery game pins down the provider.
  const game =
    view.kind === 'game'
      ? items.find((g) => g.code === view.code)
      : items.find((g) => g.providerId);

  const launchKey = game ? `${view.kind}:${game.id}:${attempt}` : null;

  useEffect(() => {
    if (!accessToken || !game?.providerId || !launchKey) return;
    if (launchedRef.current === launchKey) return;
    launchedRef.current = launchKey;
    setFailed(false);

    void (async () => {
      try {
        const outcome = await launchGame({
          providerId: game.providerId,
          gameId: game.id,
          lang: i18n.language ?? 'th',
          path: view.kind === 'tickets' ? 'lottery-tickets' : view.kind === 'results' ? 'lottery-results' : undefined,
          hint:
            game.launchMode && game.launchMethod
              ? { mode: game.launchMode, method: game.launchMethod }
              : null,
        });
        if (outcome.kind === 'iframe') {
          setEmbed({ url: outcome.url, method: outcome.method, payload: outcome.payload });
        } else if (outcome.kind === 'internal') {
          await navigate({ to: '/games/originals/$code', params: { code: outcome.code }, search: { session: outcome.session } });
        } else if (outcome.kind === 'popup') {
          // Game opened in a new tab — this page has nothing to show.
          await navigate({ to: '/lottery' });
        }
        // redirect: the current tab is being replaced; nothing to do.
      } catch (err) {
        if (err instanceof PopupBlockedError) {
          const url = err.url;
          toast.error(t('games:popupBlocked.title'), {
            action: { label: t('games:popupBlocked.openManually'), onClick: () => window.location.assign(url) },
            duration: 10_000,
          });
        } else {
          toast.error(mapApiError(err, t));
        }
        setFailed(true);
      }
    })();
  }, [accessToken, game, launchKey, view.kind, i18n.language, navigate, t]);

  if (!accessToken) return null;

  const title =
    view.kind === 'tickets'
      ? t('casino:lottery.myTickets')
      : view.kind === 'results'
        ? t('casino:lottery.results')
        : (game?.name ?? '');

  const railView: LotteryRailView = view.kind === 'game' ? 'game' : view.kind;

  if (embed) {
    return (
      <SeamlessGameFrame
        url={embed.url}
        method={embed.method}
        payload={embed.payload}
        title={title}
        game={view.kind === 'game' ? (game ?? null) : null}
        onBack={() => void navigate({ to: '/lottery' })}
        sideRail={<LotterySideRail active={railView} />}
      />
    );
  }

  const backAction = (
    <Button size="sm" onClick={() => void navigate({ to: '/lottery' })}>
      {t('common:common.back')}
    </Button>
  );

  if (gamesQuery.isError || failed) {
    return (
      <EmptyState
        icon="ticket"
        title={t('casino:lottery.launchFailed')}
        action={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (gamesQuery.isError) void gamesQuery.refetch();
                setAttempt((n) => n + 1);
              }}
            >
              {t('common:common.retry')}
            </Button>
            {backAction}
          </div>
        }
      />
    );
  }

  if (!gamesQuery.isPending && !game) {
    return <EmptyState icon="ticket" title={t('casino:lottery.notFound')} action={backAction} />;
  }

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex flex-col items-center gap-3 text-[var(--text-secondary)]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-[var(--accent)]" />
        <span className="text-[13px] font-semibold">{t('common:common.loading')}</span>
      </div>
    </div>
  );
}
